/**
 * LLMProviderSettingsPanel — LLM Provider Configuration
 *
 * Shows provider enable/disable, API gateway config, model config,
 * token limits, retry policies, and provider health status.
 */
import React from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  CheckCircle as ConnectedIcon,
  Error as ErrorIcon,
  HelpOutline as UnknownIcon,
} from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '../../../hooks/reduxHooks';
import { saveLLMConfig, fetchAIConfig } from '../../../store/aiServicesSlice';
import aiServicesConfigService, { ModelInfo } from '../../../services/aiServicesConfigService';
import { typography } from '~/theme';

interface Props {
  onSnack: (message: string, severity: 'success' | 'error' | 'info') => void;
}

const PROVIDER_META: Record<string, { name: string; desc: string; color: string }> = {
  assemblyai: {
    name: 'AssemblyAI LeMUR Gateway',
    desc: 'Routes to Anthropic Claude & OpenAI models via AssemblyAI API',
    color: '#6366f1',
  },
  openai: {
    name: 'OpenAI',
    desc: 'Direct access to OpenAI GPT models via OpenAI API',
    color: '#10b981',
  },
};

const LLMProviderSettingsPanel: React.FC<Props> = ({ onSnack }) => {
  const dispatch = useAppDispatch();
  const { llmProviders, providerHealth, loading } = useAppSelector((s) => s.aiServices);
  const [updating, setUpdating] = React.useState(false);
  const [providerModels, setProviderModels] = React.useState<Record<string, ModelInfo[]>>({});
  const [modelsLoading, setModelsLoading] = React.useState(false);

  React.useEffect(() => {
    const fetchModels = async () => {
      setModelsLoading(true);
      try {
        const [assemblyai, openai] = await Promise.allSettled([
          aiServicesConfigService.getProviderModels('assemblyai'),
          aiServicesConfigService.getProviderModels('openai'),
        ]);
        setProviderModels({
          assemblyai: assemblyai.status === 'fulfilled' ? assemblyai.value.models : [],
          openai: openai.status === 'fulfilled' ? openai.value.models : [],
        });
      } catch {
        // silently degrade — models section will be empty
      } finally {
        setModelsLoading(false);
      }
    };
    fetchModels();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const getHealthStatus = (providerId: string) =>
    providerHealth.find((h) => h.provider === providerId);

  const handleToggleProvider = async (providerId: string) => {
    const provider = llmProviders.find((p) => p.provider_id === providerId);
    if (!provider) return;

    setUpdating(true);
    try {
      // Toggle between assemblyai and openai as primary
      const newProvider = providerId === 'assemblyai' ? 'assemblyai' : 'openai';
      await dispatch(saveLLMConfig({ primary_provider: newProvider })).unwrap();
      await dispatch(fetchAIConfig()).unwrap();
      onSnack(`${providerId === 'assemblyai' ? 'AssemblyAI' : 'OpenAI'} set as primary provider`, 'success');
    } catch (err) {
      onSnack(`Failed to update provider: ${err}`, 'error');
    } finally {
      setUpdating(false);
    }
  };

  // Determine which provider is currently active based on LLM_PROVIDER config
  const activeProvider = llmProviders.find((p) => p.enabled)?.provider_id || 'assemblyai';

  return (
    <Box sx={{ p: 3, maxWidth: 1000, mx: 'auto' }}>
      <Stack spacing={3}>
        {/* Header */}
        <Box>
          <Typography variant="h6">LLM Provider Configuration</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage LLM provider connections, API gateways, and model settings
          </Typography>
        </Box>

        <Divider />

        {/* Provider Health Overview */}
        <Card variant="outlined">
          <CardHeader
            title="Provider Status"
            titleTypographyProps={{ variant: 'subtitle1', fontWeight: typography.label.fontWeight }}
          />
          <CardContent>
            <Grid container spacing={2}>
              {providerHealth.map((ph) => {
                const isConnected = ph.status === 'connected';
                const isError = ph.status === 'error';
                return (
                  <Grid item xs={12} md={6} key={ph.provider}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: isConnected ? 'success.50' : isError ? 'error.50' : 'grey.50',
                        border: '1px solid',
                        borderColor: isConnected ? 'success.200' : isError ? 'error.200' : 'grey.200',
                      }}
                    >
                      {isConnected ? (
                        <ConnectedIcon sx={{ color: 'success.main' }} />
                      ) : isError ? (
                        <ErrorIcon sx={{ color: 'error.main' }} />
                      ) : (
                        <UnknownIcon sx={{ color: 'grey.500' }} />
                      )}
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={typography.button.fontWeight}>{ph.display_name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {isConnected ? 'Connected' : isError ? ph.error || 'Connection failed' : 'Unknown'}
                        </Typography>
                      </Box>
                      <Chip
                        label={ph.status}
                        size="small"
                        color={isConnected ? 'success' : isError ? 'error' : 'default'}
                      />
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </CardContent>
        </Card>

        {/* Provider Detail Cards */}
        {llmProviders.map((provider) => {
          const meta = PROVIDER_META[provider.provider_id] || {
            name: provider.provider_id,
            desc: '',
            color: '#64748b',
          };
          const health = getHealthStatus(provider.provider_id);

          return (
            <Card
              key={provider.provider_id}
              variant="outlined"
              sx={{
                borderLeft: `4px solid ${meta.color}`,
                opacity: provider.enabled ? 1 : 0.6,
              }}
            >
              <CardHeader
                title={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="subtitle1" fontWeight={typography.label.fontWeight}>{meta.name}</Typography>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Switch
                        size="small"
                        checked={provider.provider_id === activeProvider}
                        onChange={() => handleToggleProvider(provider.provider_id)}
                        disabled={updating}
                      />
                      <Chip
                        label={provider.provider_id === activeProvider ? 'Primary' : 'Secondary'}
                        size="small"
                        color={provider.provider_id === activeProvider ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </Stack>
                    {health && (
                      <Chip
                        label={health.status === 'connected' ? '✓ Connected' : '✗ Error'}
                        size="small"
                        color={health.status === 'connected' ? 'success' : 'error'}
                      />
                    )}
                  </Stack>
                }
                subheader={meta.desc}
              />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="API Endpoint"
                      value={provider.api_endpoint || ''}
                      InputProps={{ readOnly: true }}
                      helperText="Configured via environment variable"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Default Model"
                      value={provider.default_model || ''}
                      InputProps={{ readOnly: true }}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={1.5}>
                    <TextField
                      fullWidth
                      label="Max Retries"
                      value={provider.max_retries}
                      InputProps={{ readOnly: true }}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={1.5}>
                    <TextField
                      fullWidth
                      label="Timeout (ms)"
                      value={provider.timeout_ms}
                      InputProps={{ readOnly: true }}
                      size="small"
                    />
                  </Grid>
                </Grid>

                {/* Security note */}
                <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    🔒 API keys are managed via environment variables and are never displayed in the UI.
                    Update API keys through your deployment configuration or .env file.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          );
        })}

        {/* Model Capabilities */}
        <Card variant="outlined">
          <CardHeader
            title="Model Capabilities"
            titleTypographyProps={{ variant: 'subtitle1', fontWeight: typography.label.fontWeight }}
          />
          <CardContent>
            {modelsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={20} />
              </Box>
            ) : (
              <Grid container spacing={2}>
                {[
                  { key: 'assemblyai', label: 'AssemblyAI Gateway Models' },
                  { key: 'openai', label: 'OpenAI Models' },
                ].map(({ key, label }) => {
                  const models = providerModels[key] ?? [];
                  return (
                    <Grid item xs={12} md={6} key={key}>
                      <Typography variant="subtitle2" gutterBottom>{label}</Typography>
                      <Stack spacing={0.5}>
                        {models.length === 0 ? (
                          <Typography variant="caption" color="text.secondary">No models available</Typography>
                        ) : (
                          models.map((m) => (
                            <Typography key={m.id} variant="caption" sx={{ fontFamily: 'monospace' }}>
                              • {m.id}
                            </Typography>
                          ))
                        )}
                      </Stack>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
};

export default LLMProviderSettingsPanel;
